"use client";

import React, { useState, useRef } from "react";
import { X, CheckCircle2, Play, Send, Calendar, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "./technical-components";

interface BookDemoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BookDemoModal({ isOpen, onClose }: BookDemoModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    institutionName: "",
    city: "",
    role: "PRINCIPAL",
    studentCount: "1000+",
    facilities: [] as string[],
    notes: "",
  });

  const videoRef = useRef<HTMLVideoElement>(null);

  if (!isOpen) return null;

  const facilityOptions = [
    "Synthetic Running Track",
    "Football / Cricket Turf",
    "Olympic / Semi-Olympic Pool",
    "Indoor Badminton / Squash Courts",
    "Basketball Arena",
    "Gymnastics / Fitness Center",
  ];

  const handleFacilityToggle = (facility: string) => {
    setFormData((prev) => ({
      ...prev,
      facilities: prev.facilities.includes(facility)
        ? prev.facilities.filter((f) => f !== facility)
        : [...prev.facilities, facility],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.institutionName) {
      toast.error("PLEASE FILL IN ALL MANDATORY FIELDS");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/book-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      // Also backup to browser localStorage
      try {
        const existing = JSON.parse(localStorage.getItem("olympio_demo_requests") || "[]");
        const newRequest = {
          ...formData,
          id: `DEMO-${Date.now()}`,
          submittedAt: new Date().toISOString(),
        };
        localStorage.setItem("olympio_demo_requests", JSON.stringify([newRequest, ...existing]));
      } catch {
        // localStorage fallback
      }

      setSubmitted(true);
      toast.success("DEMO DISPATCH REQUEST REGISTERED AND SAVED!");
    } catch (err) {
      console.error("Failed to save demo dispatch:", err);
      // Still show success if local storage worked
      setSubmitted(true);
      toast.success("DEMO DISPATCH SAVED LOCALLY!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto font-mono-tech">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[#000000]/80 backdrop-blur-md transition-opacity"
        onClick={onClose}
      />

      {/* Technical Modal Dialog Container */}
      <div className="relative w-full max-w-4xl bg-[#F7F7F5] border border-[#3A3A38]/40 shadow-none overflow-hidden z-10 my-8 text-[#1A3C2B] p-6 sm:p-8 rounded-none">
        {/* L-Corner Markers */}
        <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#1A3C2B]" />
        <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#1A3C2B]" />
        <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#1A3C2B]" />
        <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#1A3C2B]" />

        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-[#3A3A38]/20 pb-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 border-2 border-[#1A3C2B] bg-white overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/media/olympio_logo.jpeg.png"
                alt="Olympio Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="font-space font-bold text-xl sm:text-2xl text-[#1A3C2B] tracking-tight uppercase block">
                BOOK ENTERPRISE DEMONSTRATION
              </span>
              <span className="text-[10px] sm:text-[11px] text-[#3A3A38]/80 uppercase tracking-widest block -mt-0.5 font-bold">
                OLYMPIO K-12 SPORTS ERP &amp; BIOMETRIC TALENT RADAR
              </span>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 border border-[#3A3A38]/30 bg-white text-[#1A3C2B] hover:bg-[#1A3C2B] hover:text-white transition rounded-none"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-[#1A3C2B] text-white flex items-center justify-center rounded-none">
              <CheckCircle2 className="w-8 h-8 text-[#9EFFBF]" />
            </div>

            <div className="space-y-2">
              <span className="px-3 py-1 bg-[#1A3C2B]/10 border border-[#1A3C2B]/30 text-[#1A3C2B] text-[10px] font-bold uppercase tracking-widest">
                DEMO DISPATCH SAVED &amp; CONFIRMED
              </span>
              <h3 className="font-space font-bold text-3xl text-[#1A3C2B] uppercase">
                WELCOME TO RACE TO OLYMPICS
              </h3>
              <p className="text-xs text-[#3A3A38] max-w-md mx-auto leading-relaxed">
                Thank you, <span className="font-bold text-[#1A3C2B]">{formData.name}</span>. Our founding sports tech advisory team for <span className="font-bold text-[#1A3C2B]">{formData.institutionName || "your institution"}</span> has received your demonstration dispatch.
              </p>
            </div>

            <div className="border border-[#3A3A38]/20 bg-white p-4 max-w-md mx-auto text-left font-mono-tech text-xs space-y-1">
              <div className="flex justify-between border-b border-[#3A3A38]/10 pb-1 text-[#1A3C2B]">
                <span className="font-bold uppercase">DISPATCH_REF_ID:</span>
                <span className="font-bold">OLY-DEMO-{Math.floor(1000 + Math.random() * 9000)}</span>
              </div>
              <div className="text-[11px] text-[#3A3A38] pt-1">
                CONTACT DIRECT LINE: +91 9958382933 | +91 7051594695
              </div>
            </div>

            <button
              onClick={() => {
                setSubmitted(false);
                onClose();
              }}
              className="px-6 py-2.5 bg-[#1A3C2B] text-white uppercase text-xs font-bold rounded-none"
            >
              CLOSE WINDOW
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Side: Product Intro Video Preview Panel */}
            <div className="lg:col-span-5 border border-[#3A3A38]/30 bg-white p-4 space-y-4 rounded-none">
              <div className="flex items-center justify-between border-b border-[#3A3A38]/10 pb-2 text-[10px] uppercase font-bold text-[#1A3C2B]">
                <span>PRODUCT_INTRO_FILM.MP4</span>
                <span className="w-2 h-2 bg-[#1A3C2B]" />
              </div>

              {/* Video Player Streaming final.mp4 */}
              <div className="relative aspect-video bg-slate-950 border border-[#3A3A38]/20 overflow-hidden">
                <video
                  ref={videoRef}
                  src="/media/final.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-2 left-2 bg-[#1A3C2B] text-[#9EFFBF] font-mono-tech text-[9px] uppercase px-2 py-0.5 font-bold">
                  PLAYING DEMO REEL
                </div>
              </div>

              <div className="space-y-2 text-xs text-[#3A3A38]">
                <h4 className="font-space font-bold text-[#1A3C2B] uppercase">
                  WHAT YOU WILL SEE IN LIVE DEMO:
                </h4>
                <ul className="space-y-1 text-[11px]">
                  <li>✓ 6-Station Eurofit Biometric Radar</li>
                  <li>✓ Mirwald PHV Growth Spurt Engine</li>
                  <li>✓ QR Code Gate Passes &amp; Facility Monetization</li>
                  <li>✓ Parent Sports Report Card App</li>
                </ul>
              </div>

              <div className="border-t border-[#3A3A38]/10 pt-2 text-[10px] text-[#1A3C2B] font-bold uppercase">
                DIRECT FOUNDERS DIRECTORY:
                <div className="text-[#3A3A38] font-normal mt-0.5">
                  Olympiocampus@gmail.com | CEO@olympio.in
                </div>
              </div>
            </div>

            {/* Right Side: Demo Dispatch Form */}
            <form onSubmit={handleSubmit} className="lg:col-span-7 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#1A3C2B] font-bold block mb-1">
                  01. FULL NAME *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Dr. Rajesh Sharma"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-white border border-[#3A3A38]/30 p-2.5 text-xs text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#1A3C2B] font-bold block mb-1">
                    02. WORK EMAIL *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="principal@school.edu.in"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-white border border-[#3A3A38]/30 p-2.5 text-xs text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                  />
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-wider text-[#1A3C2B] font-bold block mb-1">
                    03. PHONE / WHATSAPP *
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 9958382933"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-white border border-[#3A3A38]/30 p-2.5 text-xs text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#1A3C2B] font-bold block mb-1">
                  04. SCHOOL / UNIVERSITY NAME *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Delhi Public School"
                  value={formData.institutionName}
                  onChange={(e) => setFormData({ ...formData, institutionName: e.target.value })}
                  className="w-full bg-white border border-[#3A3A38]/30 p-2.5 text-xs text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                />
              </div>

              {/* Select Sports Facilities */}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#1A3C2B] font-bold block mb-1">
                  05. SELECT CAMPUS FACILITIES TO MONETIZE / DIGITIZE
                </label>
                <div className="grid grid-cols-2 gap-1.5 pt-1">
                  {facilityOptions.map((fac) => (
                    <button
                      key={fac}
                      type="button"
                      onClick={() => handleFacilityToggle(fac)}
                      className={`p-2 text-[10px] text-left uppercase border transition rounded-none ${
                        formData.facilities.includes(fac)
                          ? "border-[#1A3C2B] bg-[#1A3C2B] text-white font-bold"
                          : "border-[#3A3A38]/20 bg-white text-[#3A3A38] hover:border-[#1A3C2B]"
                      }`}
                    >
                      {fac}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition uppercase tracking-wider font-bold text-xs rounded-none flex items-center justify-center gap-2 mt-2"
              >
                <span>{loading ? "DISPATCHING REQUEST..." : "SUBMIT DEMO DISPATCH REQUEST"}</span>
                <Send className="w-3.5 h-3.5 text-[#9EFFBF]" />
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
